import { FC, useEffect, useState } from "react";
import { IdTableVariant, LoadingStatus } from "../../../@types/enumsGlobal";
import DefaultLotsCommandsPanel from "../../../components/commandsPanels/DefaultLotsCommandsPanel";
import { DefaultGrid } from "../../../components/grids/default/defaultGrid";
import { PluginWrapper, ToolbarWrapper } from "../../../components/plugins";
import { Spinner } from "../../../components/spiner/Spinner";
import { IEntitySimpleDTO } from "../../../libs/coreapi-dto/@types/common";
import { ILotViewDTO } from "../../../libs/coreapi-dto/accounting/lot";
import { ISerialDTO } from "../../../libs/coreapi-dto/dirs/serial";
import { InvoiceDataProvider } from "../../../Services/DataProviders/InvoiceDataProvider";
import { LotDataProvider } from "../../../Services/DataProviders/LotDataProvider";
import { GosContractDataProvider } from "../../../Services/DataProviders/GosContractDataProvider";
import useGridFilter, { GridFilterAction } from "../../../system/hooks/useGridFilter";
import { useAppContext } from "../../../system/providers/appContextProvider";
import { usePluginContext } from "../../../system/providers/plugin";
import { SeriesModalWindow } from "../../Dictionaries/Series/SeriesAndCertificateModal/SeriesModalWindow";
import { GtdModalWindow } from "./gtdModal/gtdModal";
import { SerialDataProvider } from "../../../Services/DataProviders/SerialDataProvider";
import { GoodsDataProvider } from "../../../Services/DataProviders/GoodsDataProvider";
import { KizExchangeGridModal } from "../KizExchangeStatus/Modal/KizExchangeGridModal";
import Tooltip from "../../../components/tooltips/Tooltip";
import { EmptySquareButton } from "../../../components/buttons/iconButtons/editor/ActionCollection";
import { AdditionalButton } from "../../../components/buttons/iconButtons/other/PanelCollection";
import { FilePlusButton } from "../../../components/buttons/iconButtons/other/FileAndFolderCollection";
import { SelectControl } from "../../../components/controls/SelectControl";
import { IOption } from "../../../components/selects/select";
import { DatePeriodPanel } from "../../../hoc/DatePeriodPanel/DatePeriodPanel";
import { DateInput } from "../../../components/controls/inputs";
import { IDatePeriod } from "../../../components/datePeriodPicker/datePeriodPicker";
import { DateTime } from "luxon";
import BaseCommandsPanel from "../../../components/commandsPanels/BaseCommandsPanel";
import { ReportActionButton } from "../../Reports/Components/ReportActionButton/ReportActionButton";
import { IKeyValuePair } from "../../Reports/Shared/Interfaces/IKeyValuePair";
import { getFiltersForPrint } from "../../../system/functions/getFiltersForPrint";
import { GosContractSearchSelectorModal } from "../../Dictionaries/GosContract/components/GosContractSearchSelectorModal";
import { DoneAll } from "../../../libs/corporate/icons/outlined/action/MainCollection";
import styles from './styles/LotsCreatorModal.module.scss'
import renderGlobalAlert from "../../../system/hooks/useGlobalAlert";
import FinancialSecuritySelectorModal from "../../Dictionaries/FinancialSecurity/FinancialSecuritySelectorModal";
import { FinancialSecurityModalWindow } from "../../Dictionaries/FinancialSecurity/FinancialSecurityModalWindow";

type ILotViewDTOWithDisplay = ILotViewDTO & {
    seriesBestBeforeDisplay: string | null;
};

const returnGridFilter = (baseGridFilter: IGridFilter | undefined, isModal: boolean | undefined, onlyLotRemain: boolean | undefined): IGridFilter | undefined => {
    if (baseGridFilter && isModal && (onlyLotRemain || onlyLotRemain === undefined)) {
        return {
            ...baseGridFilter,
            orderDesc: false,
            columnFilters: [
                ...baseGridFilter.columnFilters,
                {
                    name: "deleted",
                    operator: "Eq",
                    value: false,
                },
                {	
                    name: "quantityRem",	
                    operator: "More",	
                    value: 0,	
                },
            ],
        };
    } else if (baseGridFilter && isModal && !onlyLotRemain) {
        return {
            ...baseGridFilter,
            orderDesc: false,
            columnFilters: [
                ...baseGridFilter.columnFilters,
                {
                    name: "deleted",
                    operator: "Eq",
                    value: false,
                },
            ],
        };
    } else if (baseGridFilter) {
        return baseGridFilter;
    } else {
        return undefined;
    }
};

const LotsGrid: FC<IGridProps> = (props) => {
    const appCtx = useAppContext();
    const pluginCtx = usePluginContext();

    const lotDataProvider = new LotDataProvider(appCtx.coreApiService);
    const invoiceDP = new InvoiceDataProvider(appCtx.coreApiService);
    const sdp = new SerialDataProvider(appCtx.coreApiService);
    const gdp = new GoodsDataProvider(appCtx.coreApiService);
    const gosContractDP = new GosContractDataProvider(appCtx.coreApiService);

    const [data, setData] = useState<ILotViewDTOWithDisplay[]>([]);
    const [gridFilter, dispatchGridFilter] = useGridFilter(returnGridFilter(props.baseGridFilter, props.isModal, props.onlyLotRemain));

    const [gridCtxFilter, dispatchGridCtxFilter] = useGridFilter();
    const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>(LoadingStatus.InProcess);
    const [totalCount, setTotalCount] = useState(0);
    const [selectedItem, setSelectedItem] = useState<IGridRow | undefined>(undefined);
    const [selectedItems, setSelectedItems] = useState<IGridRow[]>(props.data ?? []);
    const [multipleSelect, setMultipleSelect] = useState(false);
    const [docId, setDocId] = useState<string | null>(null);
    const [gtd, setGtd] = useState<boolean>(false);
    const [financialSecurity, setFinancialSecurity] = useState<boolean>(false);
    const [series, setSeries] = useState(false);
    const [kizExchange, setKizExchange] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(true);

    const [goods, setGoods] = useState<IEntitySimpleDTO | undefined>();
    const [itemSeries, setItemSeries] = useState<ISerialDTO | undefined>();
    const [gosContractModal, setGosContractModal] = useState<JSX.Element>(<></>);

    const createColumnFilter = () => {
        let newColumnFilters: IGridColumnFilter[] = [] as IGridColumnFilter[];
        newColumnFilters.push(...pluginCtx.lots.gridFilter.columnFilters);

        newColumnFilters.push({
            name: "idGoodsGlobal",
            operator: "Eq",
            value: pluginCtx.masterGrid.selectedItem?.idGoodsGlobal,
        });

        return newColumnFilters;
    };

    useEffect(() => {
        if (props.isModal) getData(gridFilter)
    }, [gridFilter]);

    useEffect(() => {
        let idGoods = pluginCtx.masterGrid.selectedItem?.idGoodsGlobal;
        if (!idGoods) {
            return;
        }
        setIsSubmitting(true);
        dispatchGridCtxFilter({ type: "sortByFilters", payload: { gridColumnFilter: createColumnFilter() } });

        gdp.getById(idGoods, (goods)=> setGoods({idGlobal: idGoods as string, displayName: goods.goodsName.name}));
        setItemSeries(undefined);
        setDocId(null);
        setSelectedItem(undefined);

    }, [pluginCtx.lots.gridFilter, pluginCtx.masterGrid.selectedItem]);

    useEffect(() => {
        if (gridCtxFilter.columnFilters.length !== 0) getData(gridCtxFilter)   
    }, [gridCtxFilter]);

    function getData(gridFilter: IGridFilter) {
        setIsSubmitting(true);
        lotDataProvider.getView(gridFilter, (data, totalCount) => {

            const formattedData: ILotViewDTOWithDisplay[] = data.map(item => {
                const { seriesBestBefore, seriesBestBeforeDisplay } = formatBestBeforeDate(
                    item.seriesBestBefore as unknown as string | null
                );
    
                return {
                    ...item,
                    seriesBestBefore,
                    seriesBestBeforeDisplay,
                };
            });
            
            setData(formattedData);
            setTotalCount(totalCount);
            data.length > 0 ? setLoadingStatus(LoadingStatus.Completed) : setLoadingStatus(LoadingStatus.NoData);
            setIsSubmitting(false);
        });
    }

    function updateSeriesForLot(idSeries)
    {
        if (selectedItem)
        {
            lotDataProvider.updateSeries(selectedItem.idGlobal, idSeries, ()=>{
            })
        }
    }

    function formatBestBeforeDate(rawValue: string | null | undefined) {
        if (!rawValue || rawValue.startsWith("0001-01-01")) {
            return {
                seriesBestBefore: null,
                seriesBestBeforeDisplay: null,
            };
        }
    
        const bestBeforeDate = DateTime.fromISO(rawValue);
        return {
            seriesBestBefore: bestBeforeDate,
            seriesBestBeforeDisplay: bestBeforeDate.toFormat("dd.MM.yyyy"),
        };
    }

    useEffect(() => {
        if (selectedItem) 
        {
            lotDataProvider.getById(selectedItem.idGlobal, (e) => {
                if (e.docNum) {
                    invoiceDP.getView(
                        {
                            numberPerPage: 10,
                            pageNumber: 1,
                            columnFilters: [{ name: "mnemocode", operator: "Eq", value: e.docNum }],
                        },
                        (e) => {
                            setDocId(e[0]?.idGlobal);
                        }
                    );
                } else {
                    setDocId(null);
                }
                if (e.idSeriesExternalGlobal)
                    sdp.getById(e.idSeriesExternalGlobal, (e) => setItemSeries(e));
                else
                    setItemSeries(undefined);
            });
        } 
        else 
        {
            setDocId(null);
            setItemSeries(undefined);
        }
    }, [selectedItem]);

    const pluginCtxWithId: IGridFilter = {
      ...pluginCtx.lots.gridFilter,
      columnFilters: [...pluginCtx.lots.gridFilter.columnFilters, { name: "idGoodsGlobal", operator: "Eq", value: pluginCtx.masterGrid.selectedItem?.idGoodsGlobal }],
    };

    const addFilter = (GridFilterAction: GridFilterAction) => {
        dispatchGridFilter(GridFilterAction)
        props.dispatchGridFilter?.(GridFilterAction)
    }

        // Очистка данных при смене груп. обработки.
        const handlerGroupClick = () => {
            setMultipleSelect(prev => !prev);
            setSelectedItems([]);
            setSelectedItem(undefined);
            props?.onMultipleSelect?.([]);
            props.onSelect?.(undefined)
        }
    const handleAddGosContract = () => {
        const lots = multipleSelect && selectedItems.length > 0
            ? selectedItems.map(item => ({
                idGoodsExternalGlobal: item.idGoodsGlobal,
                retailPriceIncVat: Number(item.cells.find(c => c.propertyName === 'retailPriceIncVat')?.value) || 0
            }))
            : [{
                idGoodsExternalGlobal: selectedItem?.idGoodsGlobal,
                retailPriceIncVat: Number(selectedItem?.cells.find(c => c.propertyName === 'retailPriceIncVat')?.value) || 0
            }];
        const body: IViewBody = {
            numberPerPage: gridCtxFilter.numberPerPage ?? 15,
            pageNumber: gridCtxFilter.pageNumber ?? 1,
            columnFilters: [],
            paramFilter: { lots },
        };
        gosContractDP.viewByLots(body, (gosContractData, totalCount) => {
            setGosContractModal(
                <GosContractSearchSelectorModal
                    data={gosContractData}
                    totalCount={totalCount}
                    ok={(e) => {
                        if (e.idGlobal) {
                            lotDataProvider.updateGosContract(e.idGlobal, multipleSelect ? selectedItems.map(i => i.idGlobal) : [selectedItem?.idGlobal as string], () => {
                                renderGlobalAlert({
                                    variant: "info",
                                    statusCode: 200,
                                    title: `Товар привязан к новому ГК`
                                });
                                setGosContractModal(<></>);
                                getData(gridCtxFilter);
                            });
                        }
                    }}
                    cancel={() => setGosContractModal(<></>)}
                />
            );
        });
    };

        //Включение груп. обработки при нажатии гор. клавиш.
        const handleToggleMultipleSelect = () => { 
            setMultipleSelect(true);
            setSelectedItem(undefined);
            props.onSelect?.(undefined)
        };

    const seriesBestBeforeOptions = [
        { displayName: 'Выбрать', value: '' },
        { displayName: 'Между', value: 'Between' },
        { displayName: 'Больше', value: 'More' },
        { displayName: 'Меньше', value: 'Less' },
        { displayName: 'Равно', value: 'Eq' }
    ] as IOption[];

    const DefaultDatePeriod = {
        startDate: DateTime.now().plus({ month: -3 }),
        endDate: DateTime.now(),
    } as IDatePeriod;
    const [seriesBestBeforeOption, setSeriesBestBeforeOption] = useState<IOption>(seriesBestBeforeOptions[0]);
    const [datePeriodSeriesBestBefore, setDatePeriodSeriesBestBefore] = useState<IDatePeriod>(DefaultDatePeriod);
    const [dateSeriesBestBefore, setDateSeriesBestBefore] = useState<DateTime | undefined | null>(DateTime.now());

    // Функционал для работы с периодом срока годности.
    const addOtherFilters = (option: IOption) => {
        let newColumnFilters: IGridColumnFilter[] = [] as IGridColumnFilter[];
        if (option.value !== seriesBestBeforeOptions[0].value) {
            if (option.value === seriesBestBeforeOptions[1].value) {
                newColumnFilters.push(
                    ...(datePeriodSeriesBestBefore?.startDate
                        ? [
                            {
                                name: "seriesBestBefore",
                                value: datePeriodSeriesBestBefore.startDate.toFormat("yyyyMMdd"),
                                operator: "MoreOrEq",
                            } as IGridColumnFilter,
                        ]
                        : []),
                    ...(datePeriodSeriesBestBefore?.endDate
                        ? [
                            {
                                name: "seriesBestBefore",
                                value: datePeriodSeriesBestBefore.endDate.plus({ days: 1 }).toFormat("yyyyMMdd"),
                                operator: "LessOrEq",
                            } as IGridColumnFilter,
                        ]
                        : []),
                );
            }
            else {
                newColumnFilters.push({
                    name: 'seriesBestBefore',
                    operator: option.value,
                    value: dateSeriesBestBefore?.toFormat("yyyyMMdd"),
                });
            }
        }
        // Удаление фильтра.
        dispatchGridFilter({ type: "deleteColumnFilter", payload: 'seriesBestBefore' })

        dispatchGridFilter({
            type: "addColumnFilters",
            payload: newColumnFilters
        });
        setSeriesBestBeforeOption(option)
    };

    const getDateComponent = (): JSX.Element => {
        switch (seriesBestBeforeOption.value) {
            case seriesBestBeforeOptions[0].value:
                return <></>
            case seriesBestBeforeOptions[1].value:
                return <DatePeriodPanel
                    datePeriod={datePeriodSeriesBestBefore}
                    onDatePeriodChange={(date: IDatePeriod) => setDatePeriodSeriesBestBefore(date)}
                />
            default:
                return <DateInput
                    value={dateSeriesBestBefore}
                    onBlur={(value) => setDateSeriesBestBefore(value)}
                    inline
                />
        }
    }

    return (
        <PluginWrapper>
            <ToolbarWrapper>
                <DefaultLotsCommandsPanel
                    pluginSettings={props.plugin}
                    gridFilter={[gridFilter, dispatchGridFilter]}
                    openGtd={() => setGtd(true)}
                    openSeries={() => setSeries(true)}
                    openKizExchange={() => setKizExchange(true)}
                    openFinancialSecurity={() => setFinancialSecurity(true)}
                    selectedItemLot={selectedItem}
                    data={data}
                    isModal={props.isModal}
                    dispatchGridFilter={(value: GridFilterAction) => addFilter(value)}
                    docId={docId}
                    disableSeries={selectedItem ? false : true}
                    disableKizExchange={isSubmitting ? true : false}
                    children={
                        <GroupProcessingButton
                            data={data}
                            multipleSelect={multipleSelect}
                            activateGroupProcessing={props.activateGroupProcessing}
                            handlerGroupClick={handlerGroupClick}
                            selectedItems={selectedItems}
                        />
                    }
                    />
                {props.isDetail && (
                    <BaseCommandsPanel groups={[
                        [{
                            iconButton: (
                                <Tooltip title="Добавить ГК">
                                    <FilePlusButton
                                        sizeVariant="mini"
                                        disabled={multipleSelect ? selectedItems.length === 0 : !selectedItem}
                                        onClick={handleAddGosContract}
                                    />
                                </Tooltip>
                            )
                        }],
                        [{
                            iconButton: (
                                <Tooltip title="Печать">
                                    <ReportActionButton
                                        caption="Печать"
                                        menuPlacement="bottomLeft"
                                        iconButton={true}
                                        disabled={!selectedItem && selectedItems.length === 0}
                                        onMenuClick={() => IdTableVariant.LotsByLots as unknown as IPlugin}
                                        onMenuItemClick={() => {
                                            const result: IKeyValuePair[] = [];
                                            if (multipleSelect && selectedItems.length > 0) {
                                                selectedItems.forEach(item => result.push({ key: item.idGlobal, value: "" }));
                                            } else if (selectedItem) {
                                                result.push({ key: selectedItem.idGlobal, value: "" });
                                            }
                                            return result;
                                        }}
                                        filters={getFiltersForPrint(props.plugin, gridCtxFilter)}
                                    />
                                </Tooltip>
                            )
                        }],
                        [{
                            iconButton: (
                                <Tooltip title="Групповая обработка">
                                    {multipleSelect
                                        ? <AdditionalButton sizeVariant="mini" onClick={handlerGroupClick} disabled={data.length === 0} />
                                        : <EmptySquareButton sizeVariant="mini" onClick={handlerGroupClick} disabled={data.length === 0} />
                                    }
                                </Tooltip>
                            )
                        }],
                    ]} />
                )}
            </ToolbarWrapper>
            {props.isModal && (
                <div style={{ display: 'flex', alignItems: 'center', gap: (seriesBestBeforeOption.value === 'Between' ? '15px' : '') }}>
                    <SelectControl
                        inline
                        label='Срок годности'
                        defaultOption={seriesBestBeforeOptions[0]}
                        value={seriesBestBeforeOption}
                        onSelect={addOtherFilters}
                        options={seriesBestBeforeOptions}
                        disabled={multipleSelect}
                    />
                    {getDateComponent()}
                </div>
            )}

            {isSubmitting ? (
                <Spinner />
            ) : (
                <DefaultGrid
                    showQuantity
                    activateGroupProcessingHotKey={props.activateGroupProcessing}
                    onMultipleSelect={(rows) => {
                            setSelectedItems(rows)
                            props?.onMultipleSelect?.(rows);
                    }}
                    multipleSelect={multipleSelect}
                    selectedItems={selectedItems}
                    onToggleMultipleSelect={handleToggleMultipleSelect}
                    gridId={props.gridId}
                    data={data}
                    loadingStatus={loadingStatus}
                    setLoadingStatus={setLoadingStatus}
                    dataProvider={lotDataProvider}
                    totalCount={totalCount}
                    filter={props?.isModal ? gridFilter : pluginCtxWithId}
                    plugin={props.plugin}
                    separator
                    singleDirectory
                    selectedItem={selectedItem}
                    selectedFirstPosition={!props.isModal ? true : false}
                    onSelect={(row) => {
                        setSelectedItem(row);
                        props.onSelect?.(row);
                    }}
                    onDoubleClick={(i) => props.onDoubleClick?.(i)}
                    onSort={(i) => (props?.isModal ? addFilter({ type: "sort", payload: i.propertyName }) : pluginCtx.lots.dispatchGridFilter({ type: "sort", payload: i.propertyName }))}                   
                    onFilterDelete={(i) =>
                        props?.isModal
                            ? addFilter({ type: "deleteColumnFilter", payload: i.propertyName })
                            : pluginCtx.lots.dispatchGridFilter({ type: "deleteColumnFilter", payload: i.propertyName })
                    }
                    onPageNumberChange={(n) =>
                        props?.isModal
                            ? addFilter({ type: "changePageNumber", payload: { pageNumber: n } })
                            : pluginCtx.lots.dispatchGridFilter({ type: "changePageNumber", payload: { pageNumber: n } })
                    }
                    onNumberPerPageChange={(n) =>
                        props?.isModal
                            ? addFilter({ type: "changeNumberPerPage", payload: { numberPerPage: n } })
                            : pluginCtx.lots.dispatchGridFilter({
                                  type: "changeNumberPerPage",
                                  payload: { numberPerPage: n },
                              })
                    }
                />
            )}
            {gtd && 
            <GtdModalWindow 
                idLotGlobal={selectedItem?.idGlobal ?? ''} 
                cancel={{ onClick: () => {
                        getData(gridCtxFilter);
                        setGtd(false);
                }}} 
                value={data.find(c => c.idGlobal === selectedItem?.idGlobal)?.gtdNumber ?? ""}
            />}
            {financialSecurity && 
            <FinancialSecurityModalWindow 
                idLotGlobal={selectedItem?.idGlobal ?? ''}
                cancel={{ onClick: () => {
                    getData(gridCtxFilter);
                    setFinancialSecurity(false);
                }}}
                />}
            {
                series &&
                <SeriesModalWindow 
                    cancel={() => setSeries(false)} 
                    goods={goods as IEntitySimpleDTO} 
                    setItemSeries={(value: ISerialDTO) => setItemSeries(value)}
                    itemSeries={itemSeries as ISerialDTO}
                    updateLot={(idSeries)=>{ updateSeriesForLot(idSeries) }}
                />
            }
            {
                kizExchange &&
                    <KizExchangeGridModal
                     idGlobal={selectedItem ? selectedItem.idGlobal : data[0].idGlobal}
                     goodsName={data.find((item) => item.idGlobal === (selectedItem ? selectedItem.idGlobal : data[0].idGlobal))?.goodsName?? ''}   
                     cancel={() => setKizExchange(false)}
                     ok={() => setKizExchange(false)}
                />
            }
            {gosContractModal}
        </PluginWrapper>
    );
};

export default LotsGrid;


export const GroupProcessingButton = ({ activateGroupProcessing, multipleSelect, handlerGroupClick, data, selectedItems }) => {
    if (!activateGroupProcessing) {
        return null;
    }
    
    return (
        <>
            <Tooltip title="Групповая обработка">
                {multipleSelect ? (
                   
                        <button
                            type="button"
                            className={styles.popoverReportButton}
                            onClick={handlerGroupClick}
                            disabled={data.length === 0}
                        >
                            <DoneAll
                                className='activePopOverReportButton'
                            />
                            {selectedItems.length > 0 && (
                                <span className={styles.itemsCount}>
                                    {selectedItems.length}
                                </span>
                            )}
                        </button>
                ) : (
                   
                        <button
                            type="button"
                            className={styles.popoverReportButton}
                            onClick={handlerGroupClick}
                            disabled={data.length === 0}
                        >
                            <DoneAll />
                        </button>
                )}
            </Tooltip>
        </>
    );
};
